/**
 * Volume Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de volume.
 *
 * Fontes:
 * - NIST SP 811 (2008)
 * - NIST Handbook 44 (2024)
 * - SI Brochure, 9th Edition (2019)
 *
 * Notas:
 * - Base: liter (L) - unidade aceita para uso com o SI
 * - 1 L = 1 dm³ = 0.001 m³
 * - Galão US vs UK: ~20% diferença (UK é maior)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type VolumeUnit =
  // SI / Métrico
  | "cubic_meter"
  | "cubic_decimeter"
  | "cubic_centimeter"
  | "cubic_millimeter"
  | "hectoliter"
  | "decaliter"
  | "liter"
  | "deciliter"
  | "centiliter"
  | "milliliter"
  | "microliter"
  // US Customary
  | "gallon_us"
  | "quart_us"
  | "pint_us"
  | "cup_us"
  | "fluid_ounce_us"
  | "tablespoon_us"
  | "teaspoon_us"
  // Imperial (UK)
  | "gallon_uk"
  | "quart_uk"
  | "pint_uk"
  | "fluid_ounce_uk"
  // Other
  | "barrel_oil"
  | "cubic_inch"
  | "cubic_foot"
  | "cubic_yard";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de volume.
 *
 * Base: liter (L)
 *
 * Fatores exatos:
 * - SI: derivados de potências de 10
 * - US: baseados em 1 gallon US = 231 in³ (exato)
 * - UK: 1 gallon UK = 4.54609 L (exato, definição 1985)
 */
export const VOLUME_UNITS: Record<VolumeUnit, SimpleUnitConfig> = {
  // SI / Métrico
  cubic_meter: {
    factor: 1000,
    symbol: "m³",
    singular: "cubic meter",
    plural: "cubic meters",
  },
  cubic_decimeter: {
    factor: 1,
    symbol: "dm³",
    singular: "cubic decimeter",
    plural: "cubic decimeters",
  },
  cubic_centimeter: {
    factor: 0.001,
    symbol: "cm³",
    singular: "cubic centimeter",
    plural: "cubic centimeters",
  },
  cubic_millimeter: {
    factor: 0.000001,
    symbol: "mm³",
    singular: "cubic millimeter",
    plural: "cubic millimeters",
  },
  hectoliter: {
    factor: 100,
    symbol: "hL",
    singular: "hectoliter",
    plural: "hectoliters",
  },
  decaliter: {
    factor: 10,
    symbol: "daL",
    singular: "decaliter",
    plural: "decaliters",
  },
  liter: {
    factor: 1,
    symbol: "L",
    singular: "liter",
    plural: "liters",
  },
  deciliter: {
    factor: 0.1,
    symbol: "dL",
    singular: "deciliter",
    plural: "deciliters",
  },
  centiliter: {
    factor: 0.01,
    symbol: "cL",
    singular: "centiliter",
    plural: "centiliters",
  },
  milliliter: {
    factor: 0.001,
    symbol: "mL",
    singular: "milliliter",
    plural: "milliliters",
  },
  microliter: {
    factor: 0.000001,
    symbol: "μL",
    singular: "microliter",
    plural: "microliters",
  },

  // US Customary (baseado em 1 gallon = 231 in³ = 3.785411784 L)
  gallon_us: {
    factor: 3.785411784,
    symbol: "gal",
    singular: "gallon",
    plural: "gallons",
  },
  quart_us: {
    factor: 0.946352946, // gallon/4
    symbol: "qt",
    singular: "quart",
    plural: "quarts",
  },
  pint_us: {
    factor: 0.473176473, // gallon/8
    symbol: "pt",
    singular: "pint",
    plural: "pints",
  },
  cup_us: {
    factor: 0.2365882365, // gallon/16
    symbol: "cup",
    singular: "cup",
    plural: "cups",
  },
  fluid_ounce_us: {
    factor: 0.0295735295625, // gallon/128
    symbol: "fl oz",
    singular: "fluid ounce",
    plural: "fluid ounces",
  },
  tablespoon_us: {
    factor: 0.01478676478125, // fl oz/2
    symbol: "tbsp",
    singular: "tablespoon",
    plural: "tablespoons",
  },
  teaspoon_us: {
    factor: 0.00492892159375, // tbsp/3
    symbol: "tsp",
    singular: "teaspoon",
    plural: "teaspoons",
  },

  // Imperial UK (1 gallon UK = 4.54609 L exato)
  gallon_uk: {
    factor: 4.54609,
    symbol: "gal (UK)",
    singular: "gallon (UK)",
    plural: "gallons (UK)",
  },
  quart_uk: {
    factor: 1.1365225, // gallon/4
    symbol: "qt (UK)",
    singular: "quart (UK)",
    plural: "quarts (UK)",
  },
  pint_uk: {
    factor: 0.56826125, // gallon/8
    symbol: "pt (UK)",
    singular: "pint (UK)",
    plural: "pints (UK)",
  },
  fluid_ounce_uk: {
    factor: 0.0284130625, // gallon/160
    symbol: "fl oz (UK)",
    singular: "fluid ounce (UK)",
    plural: "fluid ounces (UK)",
  },

  // Other
  barrel_oil: {
    factor: 158.987294928, // 42 US gallons (petroleum)
    symbol: "bbl",
    singular: "barrel",
    plural: "barrels",
  },
  cubic_inch: {
    factor: 0.016387064, // (0.0254)³ × 1000
    symbol: "in³",
    singular: "cubic inch",
    plural: "cubic inches",
  },
  cubic_foot: {
    factor: 28.316846592, // (0.3048)³ × 1000
    symbol: "ft³",
    singular: "cubic foot",
    plural: "cubic feet",
  },
  cubic_yard: {
    factor: 764.554857984, // (0.9144)³ × 1000
    symbol: "yd³",
    singular: "cubic yard",
    plural: "cubic yards",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const VOLUME_ALIASES: UnitAliases = {
  // cubic_meter
  "m³": "cubic_meter",
  m3: "cubic_meter",
  // cubic_decimeter
  "dm³": "cubic_decimeter",
  dm3: "cubic_decimeter",
  // cubic_centimeter
  "cm³": "cubic_centimeter",
  cm3: "cubic_centimeter",
  cc: "cubic_centimeter",
  // cubic_millimeter
  "mm³": "cubic_millimeter",
  mm3: "cubic_millimeter",
  // hectoliter
  hL: "hectoliter",
  hl: "hectoliter",
  // decaliter
  daL: "decaliter",
  dal: "decaliter",
  // liter
  L: "liter",
  l: "liter",
  // deciliter
  dL: "deciliter",
  dl: "deciliter",
  // centiliter
  cL: "centiliter",
  cl: "centiliter",
  // milliliter
  mL: "milliliter",
  ml: "milliliter",
  // microliter
  "μL": "microliter",
  uL: "microliter",
  ul: "microliter",
  // gallon_us
  gal: "gallon_us",
  // quart_us
  qt: "quart_us",
  // pint_us
  pt: "pint_us",
  // cup_us
  cup: "cup_us",
  // fluid_ounce_us
  "fl oz": "fluid_ounce_us",
  floz: "fluid_ounce_us",
  // tablespoon_us
  tbsp: "tablespoon_us",
  // teaspoon_us
  tsp: "teaspoon_us",
  // gallon_uk
  "gal uk": "gallon_uk",
  // quart_uk
  "qt uk": "quart_uk",
  // pint_uk
  "pt uk": "pint_uk",
  // fluid_ounce_uk
  "fl oz uk": "fluid_ounce_uk",
  // barrel_oil
  bbl: "barrel_oil",
  // cubic_inch
  "in³": "cubic_inch",
  in3: "cubic_inch",
  // cubic_foot
  "ft³": "cubic_foot",
  ft3: "cubic_foot",
  // cubic_yard
  "yd³": "cubic_yard",
  yd3: "cubic_yard",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const VOLUME_UNIT_LIST = Object.keys(VOLUME_UNITS) as VolumeUnit[];

/** Fatores de conversão (para compatibilidade) */
export const VOLUME_FACTORS: Record<VolumeUnit, number> = Object.fromEntries(
  Object.entries(VOLUME_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<VolumeUnit, number>;
